import React from "react";
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldErrors,
} from "react-hook-form";
import {
  LuAlertCircle,
  LuBoxes,
  LuPackage,
  LuRuler,
  LuTag,
  LuTrash2,
} from "react-icons/lu";

import CuttingCategoryBoxes, {
  CuttingCategoryBox,
  CuttingCategoryOption,
  itemIconClass,
  lineIconClass,
  readOnlyClass,
  rowTitleClass,
} from "../cuttingLines/CuttingCategoryBoxes";
import MaterialPhoto from "../materialLines/MaterialPhoto";
import Input from "../ui/Input";
import RateInput from "../ui/RateInput";
import cx from "../ui/cx";
import {
  arrayRemoveButtonClass,
  arrayRowClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
} from "../ui/styles";
import { formatLength, lineName } from "../../static/cuttingLine";
import {
  formatQty,
  isWholeQty,
  sanitizeQty,
} from "../../static/materialLine";
import { hasRate } from "../../static/processRate";
import { ICuttingReceiveFormInput } from "../../types/cuttingReceive";

// What the form shows with one line, worked out from the order line it receives (or, until the party's orders have
// loaded, from the receive as saved).
export type CuttingReceiveLineView = {
  key: string;
  // The item's category (0 without one): the line sits in that category's box.
  categoryId: number;
  orderId: number | null;
  code: string;
  sizeName: string | null;
  imageUrl: string | null;
  materialName: string | null;
  cutLength: number | null;
  // Only for a user who may see rates: what to show under the rate box (the order line's rate, and where it came from).
  rateHint: string | null;
  // What the order line still awaits from other receives, once the party's orders have loaded.
  pending: number | null;
  // Why the line can't be saved as it is, if it can't.
  problem: string | null;
};

// The material purchase row's checkbox, for "ઓર્ડર બંધ".
const checkboxClass =
  "h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 accent-[#113F67]";

// A receive's lines can always be taken off.
const neverLocked = () => false;

type RowProps = {
  control: Control<ICuttingReceiveFormInput>;
  errors: FieldErrors<ICuttingReceiveFormInput>;
  index: number;
  // Its place in its box, from 1.
  number: number;
  view: CuttingReceiveLineView;
  canPick: boolean;
  pickerLabel: string;
  withRates: boolean;
  sizeUnit: string;
  onRemove: (index: number) => void;
  onOpenPicker: () => void;
  onChangeQty: (index: number, previous: string, qty: string) => void;
};

// One line, laid out like an order row: its photo, the item (a button that opens its category's picker), the cut length
// or for a user who may see rates the rate, and the pieces received with "ઓર્ડર બંધ". Its order is named after the
// row's number.
const ReceiveLineRow = ({
  control,
  errors,
  index,
  number,
  view,
  canPick,
  pickerLabel,
  withRates,
  sizeUnit,
  onRemove,
  onOpenPicker,
  onChangeQty,
}: RowProps) => {
  const qtyError = errors?.items?.[index]?.qty;
  const rateError = errors?.items?.[index]?.rate;
  const name = lineName(view.code, view.sizeName);

  const removeButton = (
    <button
      type="button"
      title="કાઢી નાખો"
      className={arrayRemoveButtonClass}
      onClick={() => onRemove(index)}
    >
      <LuTrash2 className="h-5 w-5" />
    </button>
  );

  return (
    <div className={arrayRowClass}>
      <div className={rowTitleClass}>
        <span className="flex flex-wrap items-center gap-x-2">
          આઈટમ - {number}
          {view.orderId !== null && (
            <span className="rounded bg-[#113F67]/10 px-1.5 py-0.5 text-xs font-semibold">
              ઓર્ડર #{view.orderId}
            </span>
          )}
        </span>
        <span className="md:hidden">{removeButton}</span>
      </div>

      {/* The item's photo comes first, so the line is known at a glance. */}
      <div className="md:col-span-1 md:self-center">
        <MaterialPhoto
          name={view.code}
          imageUrl={view.imageUrl}
          className="h-20 w-20 rounded-md border border-gray-200 md:aspect-square md:h-auto md:w-full"
          compact
        />
      </div>

      <div className="space-y-2 md:col-span-6">
        <label className={fieldLabelClass}>આઈટમ</label>
        <button
          type="button"
          disabled={!canPick}
          title={canPick ? "આઈટમ બદલો" : pickerLabel}
          onClick={onOpenPicker}
          className={cx(
            fieldInputClass(!!view.problem),
            "relative flex w-full items-center bg-white text-left disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
          )}
        >
          <LuPackage className={itemIconClass} />
          <span className="truncate">{name}</span>
        </button>
        <p className="truncate text-xs text-gray-500">
          મટીરીયલ: {view.materialName || "-"}
          {withRates &&
            ` • લંબાઈ: ${
              view.cutLength !== null
                ? formatLength(view.cutLength, sizeUnit)
                : "-"
            }`}
        </p>
      </div>

      {withRates ? (
        <div className="space-y-2 md:col-span-2">
          <label
            htmlFor={`cutting-receive-rate-${index}`}
            className={fieldLabelClass}
          >
            ભાવ <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name={`items.${index}.rate`}
            rules={{ validate: (value) => hasRate(value) }}
            render={({ field: rateField }) => (
              <RateInput
                ref={rateField.ref}
                id={`cutting-receive-rate-${index}`}
                name={rateField.name}
                value={rateField.value}
                placeholder="0.00"
                hasError={!!rateError}
                onChange={rateField.onChange}
                onBlur={rateField.onBlur}
                icon={LuTag}
                iconClassName={lineIconClass}
                className="disabled:cursor-not-allowed disabled:bg-gray-100"
              />
            )}
          />
          {rateError ? (
            <p className={fieldErrorTextClass}>
              <LuAlertCircle className="h-4 w-4 mr-1" />
              ભાવ દાખલ કરો (0 પણ ચાલે)
            </p>
          ) : (
            view.rateHint && (
              <p className="truncate text-xs text-gray-500">{view.rateHint}</p>
            )
          )}
        </div>
      ) : (
        <div className="space-y-2 md:col-span-2">
          <label className={fieldLabelClass}>કટીંગ લંબાઈ</label>
          <div className="relative">
            <LuRuler className={lineIconClass} />
            <div className={readOnlyClass}>
              {view.cutLength !== null
                ? formatLength(view.cutLength, sizeUnit)
                : "-"}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2 md:col-span-2">
        <label
          htmlFor={`cutting-receive-qty-${index}`}
          className={fieldLabelClass}
        >
          જથ્થો <span className="text-red-500">*</span>
        </label>
        <Controller
          control={control}
          name={`items.${index}.qty`}
          rules={{ validate: (value) => isWholeQty(value) }}
          render={({ field: qtyField }) => (
            <div className="relative">
              <LuBoxes className={lineIconClass} />
              <Input
                id={`cutting-receive-qty-${index}`}
                ref={qtyField.ref}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="નંગ"
                value={qtyField.value}
                onChange={(event) => {
                  const qty = sanitizeQty(event.target.value);

                  onChangeQty(index, qtyField.value, qty);
                  qtyField.onChange(qty);
                }}
                onBlur={qtyField.onBlur}
                className={cx(fieldInputClass(!!qtyError), "bg-white")}
              />
            </div>
          )}
        />
        {qtyError ? (
          <p className={fieldErrorTextClass}>
            <LuAlertCircle className="h-4 w-4 mr-1" />
            આખો જથ્થો દાખલ કરો
          </p>
        ) : (
          view.pending !== null && (
            <p className="text-xs text-gray-500">
              બાકી: {formatQty(view.pending)}
            </p>
          )
        )}
        <Controller
          control={control}
          name={`items.${index}.close_order`}
          render={({ field: closeField }) => (
            <label className="flex w-fit cursor-pointer select-none items-center gap-2 text-xs font-medium text-gray-600">
              <input
                ref={closeField.ref}
                type="checkbox"
                aria-label={`${name} ઓર્ડર #${view.orderId ?? "-"} બંધ`}
                className={checkboxClass}
                checked={!!closeField.value}
                onChange={(event) => closeField.onChange(event.target.checked)}
                onBlur={closeField.onBlur}
              />
              ઓર્ડર બંધ
            </label>
          )}
        />
      </div>

      <div className="hidden md:col-span-1 md:flex md:items-start md:justify-center md:pt-7">
        {removeButton}
      </div>

      {view.problem && (
        <p className={cx(fieldErrorTextClass, "md:col-span-12 mt-0")}>
          <LuAlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
          {view.problem}
        </p>
      )}
    </div>
  );
};

type Props = {
  control: Control<ICuttingReceiveFormInput>;
  fields: FieldArrayWithId<ICuttingReceiveFormInput, "items", "id">[];
  errors: FieldErrors<ICuttingReceiveFormInput>;
  views: CuttingReceiveLineView[];
  boxes: CuttingCategoryBox[];
  // The categories the party has pieces to send in, and every category's name for the box titles.
  categoryOptions: CuttingCategoryOption[];
  categoryLabel: (categoryId: number | null) => string | null;
  // What the category select says when it has nothing to offer.
  categoriesMessage: string;
  // Whether to show why a box can't be saved (after the first save).
  showBoxErrors: boolean;
  onAddBox: () => void;
  onRemoveBox: (box: CuttingCategoryBox) => void;
  onChangeBoxCategory: (key: string, categoryId: number | null) => void;
  onRemove: (index: number) => void;
  onOpenPicker: (categoryId: number) => void;
  // Called before a row's qty changes, so "ઓર્ડર બંધ" can follow it.
  onChangeQty: (index: number, previous: string, qty: string) => void;
  canPick: boolean;
  pickerLabel: string;
  withRates: boolean;
  sizeUnit: string;
};

// The receive's lines in the category boxes of the order form. Each row's pieces, "ઓર્ડર બંધ" and (for a user who may see
// rates) its rate can be changed here; the rest comes from the order line it receives.
const CuttingReceiveLines = ({
  control,
  fields,
  errors,
  views,
  boxes,
  categoryOptions,
  categoryLabel,
  categoriesMessage,
  showBoxErrors,
  onAddBox,
  onRemoveBox,
  onChangeBoxCategory,
  onRemove,
  onOpenPicker,
  onChangeQty,
  canPick,
  pickerLabel,
  withRates,
  sizeUnit,
}: Props) => (
  <CuttingCategoryBoxes
    boxes={boxes}
    categoryOptions={categoryOptions}
    categoryLabel={categoryLabel}
    categoriesMessage={categoriesMessage}
    showBoxErrors={showBoxErrors}
    isLocked={neverLocked}
    canPick={canPick}
    pickerLabel={pickerLabel}
    onAddBox={onAddBox}
    onRemoveBox={onRemoveBox}
    onChangeBoxCategory={onChangeBoxCategory}
    onOpenPicker={onOpenPicker}
    renderRow={(index, number, openPicker) => {
      const field = fields[index];
      const view = views[index];

      return (
        field &&
        view && (
          <ReceiveLineRow
            key={field.id}
            control={control}
            errors={errors}
            index={index}
            number={number}
            view={view}
            canPick={canPick}
            pickerLabel={pickerLabel}
            withRates={withRates}
            sizeUnit={sizeUnit}
            onRemove={onRemove}
            onOpenPicker={openPicker}
            onChangeQty={onChangeQty}
          />
        )
      );
    }}
  />
);

export default CuttingReceiveLines;
